import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@makemydeal/dr-ui-bricks-components';

import { VehicleCard } from '../VehicleCard/VehicleCard';
import type { SelectedDeal } from '../VehicleCard/VehicleCard';
import {
    CustomerVehiclesContainer,
    TradeInBadges,
    TradeInBadge,
    TradeInBadgeGroup,
    FooterContainer,
    FooterButtonGroup,
    SlideoutContentWrapper,
    StyledSlideout,
    StyledSelectInfo,
    TradeInHeading
} from './CustomerVehicleList.style';
import type { CustomerVehiclesListProps, GroupedDeal } from '../../types/commonTypes';
import { useCustomerVehiclesSelector } from '../../selectors';
import { middlewareMgr } from '../../store/config';
import { formatVehicleName, getUniqueTradeIns } from '../../utils/utils';

export const CustomerVehiclesList: React.FC<CustomerVehiclesListProps> = ({ onClose, showOpenDealCTA }) => {
    const dispatch = useDispatch();
    const { deals, isOpen, customerFirstName, dealsCount, currentDealId, currentDealVersion } = useCustomerVehiclesSelector();
    const showOpenDeal = showOpenDealCTA ?? true;
    const [selectedDeal, setSelectedDeal] = useState<SelectedDeal>(null);
    const title = customerFirstName ? `${customerFirstName}'s Deals (${dealsCount})` : `Deals (${dealsCount})`;

    useEffect(() => {
        if (currentDealId && currentDealVersion) {
            setSelectedDeal({ dealId: currentDealId, version: currentDealVersion });
        }
    }, [currentDealId, currentDealVersion]);

    const isCurrentDealSelected =
        selectedDeal != null && selectedDeal.dealId === currentDealId && selectedDeal.version === currentDealVersion;

    const handleSelectDeal = (dealId: string, version: string) => {
        setSelectedDeal({ dealId, version });
    };

    const handleOpenDeal = () => {
        if (selectedDeal && middlewareMgr?.hostActions?.openDeal) {
            dispatch(middlewareMgr.hostActions.openDeal({ dealId: selectedDeal.dealId, version: selectedDeal.version }));
        }
    };

    // ---- compare workflow ----
    // const { isDealListCompareEnabled } = useCustomerVehiclesSelector();
    // const isCompareEnabled = isDealListCompareEnabled ?? true;
    // const [selectedVersions, setSelectedVersions] = useState<Set<string>>(new Set());
    //
    // const handleSelectionChange = (version: string, checked: boolean) => {
    //     setSelectedVersions((prev) => {
    //         const next = new Set(prev);
    //         if (checked) {
    //             next.add(version);
    //         } else {
    //             next.delete(version);
    //         }
    //         return next;
    //     });
    // };
    //
    // const totalSelected = selectedVersions.size;
    // const isMaxSelected = totalSelected >= 3;
    // const isCompareDisabled = !isCompareEnabled || totalSelected < 2;
    //
    // const compareDeals = () => {
    //     // Implement comparison logic here
    //     // console.log('Compare button clicked');
    // };

    const onCancel = () => {
        onClose?.();
    };

    const hasTradeIns = deals.some((groupedDeal: GroupedDeal) => (groupedDeal.tradeInVehicles?.tradeIns?.length ?? 0) > 0);

    return (
        <StyledSlideout title={title} show={isOpen} data-testid="customer-search-drawer" position="right" onClose={onClose}>
            <SlideoutContentWrapper>
                {hasTradeIns && (
                    <TradeInBadges>
                        <TradeInHeading level={6} weight="bold" data-testid="trade-in-header">
                            Trade - In:
                        </TradeInHeading>
                        <TradeInBadgeGroup>
                            {getUniqueTradeIns(deals).map((tradeIn, idx) => (
                                <TradeInBadge
                                    key={`trade-in-${idx}`}
                                    $variant={tradeIn.isTradeIncluded ? 'info' : 'light'}
                                    data-variant={tradeIn.isTradeIncluded ? 'info' : 'light'}
                                    data-testid="trade-in-badge"
                                >
                                    {formatVehicleName(tradeIn)}
                                </TradeInBadge>
                            ))}
                        </TradeInBadgeGroup>
                    </TradeInBadges>
                )}
                <CustomerVehiclesContainer>
                    <StyledSelectInfo size="small" data-testid="select-deal-instruction">
                        Select one deal to open, from any vehicle below.
                    </StyledSelectInfo>
                    {deals.map((deal: GroupedDeal, index: number) => (
                        <VehicleCard key={index} {...deal} selectedDeal={selectedDeal} onSelectDeal={handleSelectDeal} />
                    ))}
                    {/* ---- compare workflow ----
                    {deals.map((deal: GroupedDeal, index: number) => (
                        <VehicleCard
                            key={index}
                            {...deal}
                            showCheckboxes={isCompareEnabled}
                            selectedVersions={selectedVersions}
                            isMaxSelected={isMaxSelected}
                            onSelectionChange={handleSelectionChange}
                            showOpenDealCTA={showOpenDeal}
                        />
                    ))} */}
                </CustomerVehiclesContainer>
            </SlideoutContentWrapper>
            <FooterContainer>
                <FooterButtonGroup>
                    <Button buttonStyle="secondary" onClick={onCancel} data-testid="cancel-button" className="cancel-button">
                        Cancel
                    </Button>
                    {showOpenDeal && (
                        <Button
                            buttonStyle="primary"
                            onClick={handleOpenDeal}
                            data-testid="open-deal-button"
                            className="open-deal-button"
                            disabled={!selectedDeal || isCurrentDealSelected}
                        >
                            Open Deal
                        </Button>
                    )}
                    {/* ---- compare workflow ----
                        {isCompareEnabled && (
                            <Button
                                buttonStyle="primary"
                                onClick={compareDeals}
                                data-testid="compare-button"
                                className="compare-button"
                                disabled={isCompareDisabled}
                            >
                                Compare ({totalSelected}/3)
                            </Button>
                        )} */}
                </FooterButtonGroup>
            </FooterContainer>
        </StyledSlideout>
    );
};

export default CustomerVehiclesList;
